import { type AbortSignalPolyfill } from 'abort-signal-polyfill';
import { ReadonlySignal } from '@funtech-int/rival';
import {
    EmotionValue,
    removeMovieEmotionMutation,
    removeMovieNotInterestedMutation,
    removeMovieWatchedMutation,
    removeMoviePlannedToWatchMutation,
    setMovieEmotionMutation,
    setMovieNotInterestedMutation,
    setMovieWatchedMutation,
    setMoviePlannedToWatchMutation,
} from '@funtech-int/kp-graphql-sdk/yango-movies-www';

import { GraphqlClient } from '@/shared/lib/graphqlClient';

import { InMemoryCache } from './inMemoryCache';
import { createSingleFlight } from './singleFlight';

// TODO: update ts https://st.yandex-team.ru/SAFTWEB-1093
declare const AbortSignal: AbortSignalPolyfill;

interface MovieUGC {
    plannedToWatch: ReadonlySignal<boolean>;
    togglePlannedToWatch(): Promise<boolean>;

    notInterested: ReadonlySignal<boolean>;
    toggleNotInterested(): Promise<boolean>;

    watched: ReadonlySignal<boolean>;
    toggleWatched(): Promise<boolean>;

    emotion: ReadonlySignal<EmotionValue | undefined>;
    setEmotion(value: EmotionValue): Promise<EmotionValue | undefined>;
}

interface MovieUGCParams {
    movieId: number;
    graphqlClient: GraphqlClient;
    inMemoryCache: InMemoryCache;
    abortSignal: AbortSignal;
}

export function getMovieUGCKey(
    movieId: number,
    type: 'plannedToWatch' | 'watched' | 'notInterested' | 'emotion'
) {
    return `${movieId}:${type}`;
}

export function createMovieUGC({
    movieId,
    graphqlClient,
    inMemoryCache,
    abortSignal,
}: MovieUGCParams): MovieUGC {
    const plannedToWatchKey = getMovieUGCKey(movieId, 'plannedToWatch');
    const plannedToWatch = inMemoryCache.getSignal(plannedToWatchKey, false);

    const plannedToWatchMutation = createSingleFlight(
        (internalAbortSignal: AbortSignal, value: boolean) =>
            inMemoryCache.update(plannedToWatchKey, value, async (ctx) => {
                ctx.stage();

                try {
                    const signal = AbortSignal.any([abortSignal, internalAbortSignal]);
                    let plannedToWatch: boolean;

                    if (value) {
                        const result = await graphqlClient.fetch(
                            { operation: setMoviePlannedToWatchMutation, variables: { movieId } },
                            { abortSignal: signal }
                        );
                        plannedToWatch = Boolean(result.data?.plannedToWatch);
                    } else {
                        const result = await graphqlClient.fetch(
                            {
                                operation: removeMoviePlannedToWatchMutation,
                                variables: { movieId },
                            },
                            { abortSignal: signal }
                        );
                        plannedToWatch = Boolean(result.data?.plannedToWatch);
                    }

                    ctx.commit();

                    return plannedToWatch;
                } catch (err) {
                    ctx.rollback();

                    throw err;
                }
            })
    );

    // ---

    const notInterestedKey = getMovieUGCKey(movieId, 'notInterested');
    const notInterested = inMemoryCache.getSignal(notInterestedKey, false);

    const notInterestedMutation = createSingleFlight(
        (internalAbortSignal: AbortSignal, value: boolean) =>
            inMemoryCache.update(notInterestedKey, value, async (ctx) => {
                ctx.stage();

                try {
                    const signal = AbortSignal.any([abortSignal, internalAbortSignal]);
                    let notInterested: boolean;

                    if (value) {
                        const result = await graphqlClient.fetch(
                            { operation: setMovieNotInterestedMutation, variables: { movieId } },
                            { abortSignal: signal }
                        );
                        notInterested = Boolean(result.data?.notInterested);
                    } else {
                        const result = await graphqlClient.fetch(
                            { operation: removeMovieNotInterestedMutation, variables: { movieId } },
                            { abortSignal: signal }
                        );
                        notInterested = Boolean(result.data?.notInterested);
                    }

                    ctx.commit();

                    return notInterested;
                } catch (err) {
                    ctx.rollback();

                    throw err;
                }
            })
    );

    // ---

    const watchedKey = getMovieUGCKey(movieId, 'watched');
    const watched = inMemoryCache.getSignal(watchedKey, false);

    const watchedMutation = createSingleFlight((internalAbortSignal: AbortSignal, value: boolean) =>
        inMemoryCache.update(watchedKey, value, async (ctx) => {
            ctx.stage();

            try {
                const signal = AbortSignal.any([abortSignal, internalAbortSignal]);
                let watched: boolean;

                if (value) {
                    const result = await graphqlClient.fetch(
                        { operation: setMovieWatchedMutation, variables: { movieId } },
                        { abortSignal: signal }
                    );
                    watched = Boolean(result.data?.watched);
                } else {
                    const result = await graphqlClient.fetch(
                        { operation: removeMovieWatchedMutation, variables: { movieId } },
                        { abortSignal: signal }
                    );
                    watched = Boolean(result.data?.watched);
                }

                ctx.commit();

                return watched;
            } catch (err) {
                ctx.rollback();

                throw err;
            }
        })
    );

    // ---

    const emotionKey = getMovieUGCKey(movieId, 'emotion');
    const emotion = inMemoryCache.getSignal(emotionKey, undefined);

    const emotionMutation = createSingleFlight(
        (internalAbortSignal: AbortSignal, value: EmotionValue) => {
            const prevValue = emotion.get();
            const targetValue = value === prevValue ? undefined : value;

            return inMemoryCache.update(emotionKey, targetValue, async (ctx) => {
                ctx.stage();

                try {
                    const signal = AbortSignal.any([abortSignal, internalAbortSignal]);
                    let emotion: EmotionValue | null | undefined;

                    if (targetValue) {
                        const result = await graphqlClient.fetch(
                            { operation: setMovieEmotionMutation, variables: { movieId, value } },
                            { abortSignal: signal }
                        );
                        emotion = result.data?.emotion;
                    } else {
                        const result = await graphqlClient.fetch(
                            {
                                operation: removeMovieEmotionMutation,
                                variables: { movieId, value },
                            },
                            { abortSignal: signal }
                        );
                        emotion = result.data?.emotion;
                    }

                    ctx.commit();

                    return emotion ?? undefined;
                } catch (err) {
                    ctx.rollback();

                    throw err;
                }
            });
        }
    );

    return {
        plannedToWatch,
        togglePlannedToWatch() {
            const value = plannedToWatch.get();
            return plannedToWatchMutation.run(!value);
        },

        notInterested,
        toggleNotInterested() {
            const value = notInterested.get();
            return notInterestedMutation.run(!value);
        },

        watched,
        toggleWatched() {
            const value = watched.get();
            return watchedMutation.run(!value);
        },

        emotion,
        setEmotion(value) {
            const currentEmotion = emotion.get();

            const promises: [Promise<EmotionValue | undefined>, Promise<boolean>?] = [
                emotionMutation.run(value),
            ];

            if (value !== currentEmotion && !watched.get()) {
                promises.push(watchedMutation.run(true));
            }

            return Promise.all(promises).then(([emotionResult]) => emotionResult);
        },
    };
}
